import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  dismissFailedUpload as reduxDismissFailedUpload,
  retryFailedUpload as reduxRetryFailedUpload,
  setComponentBlurred as reduxSetComponentBlurred,
} from '../redux/actions/checklistEdit'
import {
  ChecklistEditState,
  hasUploadsInProgress,
  PendingUpload,
  SimpleEditComponentMetadata,
} from '../redux/reducers/checklistEdit'
import { useCallback } from 'react'

type MapEditChecklistStateToProps<T> = (state: { checklist: { edit: ChecklistEditState } }) => T

/**
 * Pick checklist edit state from Redux state.
 *
 * @param state - The Redux state.
 * @returns The checklist edit state.
 */
function pickEditState(state: { checklist: { edit: ChecklistEditState } }) {
  return state.checklist.edit
}

function useEditChecklist(): ChecklistEditState {
  return useSelector(pickEditState)
}

/**
 * Use a selector to pick a value from the checklist edit state.
 *
 * @param selector - The selector to pick the value from the checklist edit state.
 * @returns The value from the checklist edit state.
 */
function useEditChecklistSelector<T>(selector: (state: ChecklistEditState) => T) {
  const _pick = useCallback(
    (state: { checklist: { edit: ChecklistEditState } }) => {
      return selector(pickEditState(state))
    },
    [selector]
  )

  return useSelector(_pick)
}

const _fallbackMetadata = (componentId: string): SimpleEditComponentMetadata => ({
  showError: false,
  error: null,
  value: null,
  disabled: false,
  id: componentId,
  required: false,
  updating: false,
})

/**
 * Redux selector in `ChecklistEditState` to get the component metadata for a given component id .
 *
 * @param {string} componentId - The id of the component to get the metadata for.
 * @returns {SimpleEditComponentMetadata} The component metadata.
 */
function useEditChecklistComponentMetadata(componentId: string): SimpleEditComponentMetadata {
  const _pick = useCallback(
    (state: ChecklistEditState) => {
      return state.componentsMetadata[componentId] ?? _fallbackMetadata(componentId)
    },
    [componentId]
  )
  return useEditChecklistSelector(_pick)
}

/**
 * The uploads (queued, in progress or failed) of the component with id {@link componentId}.
 */
function usePendingUploads(componentId: string): PendingUpload[] {
  const _pick = useCallback(
    (state: { checklist: { edit: ChecklistEditState } }) => {
      return Object.values(pickEditState(state).pendingUploads).filter(upload => upload.componentId === componentId)
    },
    [componentId]
  )

  return useSelector(_pick, shallowEqual)
}

/**
 * Whether there is at least one upload not yet concluded in the checklist.
 */
function useSomeUploadsInProgress(): boolean {
  return useEditChecklistSelector(hasUploadsInProgress)
}

/**
 * Available actions to dispatch to the checklist edit state.
 */
function useDispatchEditChecklist() {
  const dispatch = useDispatch()

  const setComponentBlurred = useCallback(
    (componentId: string) => {
      dispatch(reduxSetComponentBlurred(componentId))
    },
    [dispatch]
  )

  const dismissFailedUpload = useCallback(
    (uploadId: string) => {
      dispatch(reduxDismissFailedUpload(uploadId))
    },
    [dispatch]
  )

  const retryFailedUpload = useCallback(
    (uploadId: string) => {
      dispatch(reduxRetryFailedUpload(uploadId))
    },
    [dispatch]
  )

  return { setComponentBlurred, dismissFailedUpload, retryFailedUpload }
}

export {
  useEditChecklist,
  useEditChecklistComponentMetadata,
  useDispatchEditChecklist,
  useEditChecklistSelector,
  usePendingUploads,
  useSomeUploadsInProgress,
  pickEditState,
  MapEditChecklistStateToProps,
}
